import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { usePreviewXml, useXmlDiff } from '@/api/queries';
import XmlLine from '@/components/XmlLine';

type View = 'preview' | 'diff';

const monospace = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12.5,
  lineHeight: 1.55,
  whiteSpace: 'pre' as const,
};

/**
 * What export would emit, and how it differs from what the provider sent.
 *
 * This is the screen an operator is expected to read before approving an export, which is why the diff
 * being minimal matters so much — a churned document that reports every line as changed is worse than
 * no diff at all, because it looks like it is telling you something.
 */
export default function XmlTab({
  assetId,
  version,
}: {
  assetId: string;
  version: string;
}) {
  const [view, setView] = useState<View>('diff');
  const preview = usePreviewXml(assetId, version);
  const diff = useXmlDiff(assetId, version);

  const active = view === 'diff' ? diff : preview;
  const changedCount = diff.data?.filter((line) => line.op !== ' ').length ?? 0;

  return (
    <Stack gap={2} minWidth={0}>
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_e, next: View | null) => next && setView(next)}
        >
          <ToggleButton value="diff">Diff vs provider</ToggleButton>
          <ToggleButton value="preview">Export preview</ToggleButton>
        </ToggleButtonGroup>

        {view === 'diff' && diff.data && (
          <Typography variant="body2" color="text.secondary">
            {changedCount === 0
              ? 'Identical to what the provider sent — nothing has been overridden.'
              : `${changedCount} changed line${
                  changedCount === 1 ? '' : 's'
                } of ${diff.data.length}`}
          </Typography>
        )}
      </Stack>

      {active.isLoading && <CircularProgress size={24} />}

      {active.error && (
        <Alert severity="error">
          {active.error instanceof Error
            ? active.error.message
            : 'Failed to render XML'}
        </Alert>
      )}

      {view === 'preview' && preview.data && (
        <Box
          component="pre"
          sx={{
            ...monospace,
            m: 0,
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
            overflowX: 'auto',
            maxHeight: '60vh',
          }}
        >
          {(() => {
            const lines = preview.data.replace(/\r\n/g, '\n').split('\n');
            const gutterWidth = String(lines.length).length;
            return lines.map((line, index) => (
              <XmlLine
                key={index}
                text={line}
                lineNumber={index + 1}
                gutterWidth={gutterWidth}
              />
            ));
          })()}
        </Box>
      )}

      {view === 'diff' && diff.data && (
        <Box
          sx={{
            ...monospace,
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
            overflowX: 'auto',
            maxHeight: '60vh',
          }}
        >
          {(() => {
            // Numbered against the document export will emit, so a line number here points at the same
            // line in the Preview view. A removed line has no number because it will not be in that
            // document — numbering the rendered rows instead would give numbers that match neither
            // document, which is worse than none.
            const gutterWidth = String(
              diff.data.filter((l) => l.op !== '-').length,
            ).length;
            let emitted = 0;

            return diff.data.map((line, index) => {
              const lineNumber = line.op === '-' ? undefined : ++emitted;
              return (
                <Box
                  key={index}
                  sx={{
                    // The row tint and the marker carry the operation; the tokens carry the syntax.
                    // Previously the row colour was the only signal and it overrode everything, so the
                    // XML itself was unreadable in the view an operator is meant to read most closely.
                    bgcolor:
                      line.op === '+'
                        ? 'rgba(102, 187, 106, 0.12)'
                        : line.op === '-'
                        ? 'rgba(239, 83, 80, 0.12)'
                        : 'transparent',
                  }}
                >
                  <XmlLine
                    text={line.text}
                    lineNumber={lineNumber}
                    gutterWidth={gutterWidth}
                    marker={line.op === ' ' ? '' : line.op}
                  />
                </Box>
              );
            });
          })()}
        </Box>
      )}
    </Stack>
  );
}
